/**
 * Branded Event Detail Page Component
 *
 * This component renders the new branded event detail page UI.
 * It can be used by both:
 * - /event/[eventSlug] (branded route)
 * - /[eventSlug] (legacy route, when waffle flag is enabled)
 */

import { createLandingPageComponent } from "@app-contexts/public/features/detail-pages/utils/createLandingPageComponent";
import { eventAdapter } from "@app-contexts/public/features/detail-pages/adapters/eventAdapter";
import { useEventDetailPage } from "@app-contexts/public/features/event-detail/hooks/useEventDetailPage";
import EventAuthorization from "@shared/modules/events/components/EventAuthorization/EventAuthorization.web";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { ProductCategoryTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import type { BrandingConfigSerializer } from "@shared/modules/branding/data/types";
import { toThemeConfig } from "@shared/modules/branding/utils/ssr-branding-utils";

interface EventAuthProps {
  isLoading: boolean;
  eventData: PublicBaseEventSerializer | undefined;
  ticketsAuthorizedByPassword: number[] | null;
  updateAuthorizedTicketArray: (ids: number[] | null) => void;
}

export interface BrandedEventDetailPageProps {
  eventData: PublicBaseEventSerializer | null;
  productCategories?: ProductCategoryTypeSerializer[];
  initialBrandingConfig?: BrandingConfigSerializer | null;
}

const useEventDetailPageWithRename = ({
  eventData,
  productCategories,
  initialBrandingConfig,
}: BrandedEventDetailPageProps) =>
  useEventDetailPage({
    initialEventData: eventData,
    cachedProductCategories: productCategories,
    initialBrandingConfig,
  });

export const BrandedEventDetailPage = createLandingPageComponent<
  BrandedEventDetailPageProps,
  ReturnType<typeof useEventDetailPage>,
  EventAuthProps
>({
  usePageHook: useEventDetailPageWithRename,
  adapter: eventAdapter,
  AuthorizationWrapper: EventAuthorization,
  getAuthProps: (state, pageData) => ({
    isLoading: pageData.ui.isLoading,
    eventData: state.eventData,
    ticketsAuthorizedByPassword: state.ticketsAuthorizedByPassword,
    updateAuthorizedTicketArray: state.setTicketsAuthorizedByPassword,
  }),
  getStateKey: (state) => state.eventData?.id,
  getThemeConfig: (state) => toThemeConfig(state.venueBrandingConfig),
  getLayoutPreset: (state) => state.venueBrandingConfig?.branding_layout_preset,
});

export default BrandedEventDetailPage;
